<template>
  <div>
    <Navbar />
    <div class="bg-gradient-coral overflow-hidden nav-offset-spacing">
      <div class="standard-spacing standard-spacing-y">
        <h1 class="heading-3 text-center font-light">
          Welcome to Wellbeing & Performance, <span class="font-decorative font-normal">Elevated.</span>
        </h1>
        <div class="flex items-center justify-center mt-8">
          <p class="font-light mr-4 text-xs md:text-base">Billed Annually</p>
          <Switch
            v-model="monthly"
            class="inline-flex h-6 sm:h-8 w-12 sm:w-16 items-center rounded-full bg-turquoise-100">
            <span class="sr-only">Toggle billing</span>
            <span
              :class="monthly ? 'translate-x-7 sm:translate-x-9' : 'translate-x-1'"
              class="inline-block h-4 sm:h-6 w-4 sm:w-6 transform rounded-full bg-coral-900 transition" />
          </Switch>
          <p class="font-light ml-4 text-xs md:text-base">Billed Monthly</p>
        </div>
        <ClientOnly>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mt-8 md:h-52">
            <PricingButton
              v-if="!errorMessage"
              name="Premium"
              :info="!monthly ? 'Save 38%' : ''"
              :currency="pricingData?.currency"
              :cost="pricingData?.cost"
              detail="per person/month"
              :loading="loadingPricing"
              @click="goToBuy" />
            <div v-if="errorMessage" class="w-full flex items-center justify-center text-copper-700">
              {{ errorMessage }}
            </div>
            <PricingButton name="Enterprise" info="For teams 500+" override-label="Contact us" @click="goToContact" />
          </div>
        </ClientOnly>
        <h2 class="standard-spacing-t text-xl md:text-4xl font-extralight">Plan Inclusions</h2>
        <FeaturesTable title="Talent Features" :features="talentFeatures" class="mt-4 md:mt-8" />
        <FeaturesTable title="Leader & Organisation Features" :features="orgFeatures" class="mt-4 md:mt-8" />
        <div class="standard-spacing-t">
          <ClientOnly>
            <div class="flex items-center justify-center space-x-8">
              <HoapButton inverted label="Get started now" :url="config.public.buyUrl" external />
              <HoapButton inverted label="Book a demo" :url="config.public.calendlyURL" external />
            </div>
          </ClientOnly>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Ref } from 'vue'
  import Axios from 'axios'
  import { Switch } from '@headlessui/vue'

  const axios = Axios.create()
  const config = useRuntimeConfig()

  const monthly: Ref<boolean> = ref(false)
  const errorMessage = ref('')
  const loadingPricing = ref(false)
  const allPricingData = ref()

  onMounted(async () => {
    loadingPricing.value = true
    try {
      const { data } = await axios.get('/api/get-stripe-pricing')
      allPricingData.value = data
    } catch (e) {
      errorMessage.value = 'Failed to load pricing data'
    } finally {
      loadingPricing.value = false
    }
  })

  const pricingData = computed(() => {
    if (!allPricingData.value) return
    const data = monthly.value ? allPricingData.value.monthlyPriceData : allPricingData.value.yearlyPriceData
    return {
      currency: data.currency,
      cost: (parseInt(monthly.value ? data.unit_amount : data.unit_amount / 12) / 100).toFixed(2),
    }
  })

  const talentFeatures = [
    {
      name: 'Daily Check In',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Personal Trends',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Personal Recommendations',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Hoap Original video content',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Reach Outs',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Advocates',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Wellbeing & Performance Library',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Technical Support',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Journal',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Monthly Workplace Pulse Checks',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Personalised Talent & Team Onboarding (for over 100 users)',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Community',
      comingSoon: true,
    },
    {
      name: 'Goal Setting',
      comingSoon: true,
    },
    {
      name: 'Access to virtual Q&A chats with Hoap Experts',
      comingSoon: true,
    },
  ]

  const orgFeatures = [
    {
      name: 'Team Trends',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Advocate Trends',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Advocate Insights',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Smart Notifications',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Leader Resources',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Engagement Statistics',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Technical Support',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Monthly Workplace Pulse Checks',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Personalised Talent & Team Onboarding (for over 100 users)',
      premium: true,
      enterprise: true,
    },
    {
      name: 'Dedicated Account Manager',
      enterprise: true,
    },
    {
      name: 'Personalised Executive Leadership Coaching',
      enterprise: true,
    },
    {
      name: 'Access to Ongoing Virtual Leader Training',
      comingSoon: true,
    },
  ]

  const goToBuy = async () => window.open(config.public.buyUrl, '_blank')
  const goToContact = async () => await navigateTo({ path: '/contact-us' })

  setMetadata({ pageTitle: 'Pricing' })
</script>
